import styles from "./PulseCore.module.css";

/* ═══════════════════════════════════════════
   PULSE CORE — the pulse of Terra2
   Central nucleus + concentric pulse rings
   + orbiting collateral nodes + radar sweep
   ═══════════════════════════════════════════ */

export default function PulseCore({ size = 420 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="-200 -200 400 400"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.svg}
      role="img"
      aria-label="Solid Protocol — the pulse of Terra2"
    >
      <defs>
        <radialGradient id="pulseHalo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FF47C2" stopOpacity="0.32" />
          <stop offset="40%" stopColor="#8777F0" stopOpacity="0.18" />
          <stop offset="80%" stopColor="#6086FF" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="pulseRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#62FFD0" />
          <stop offset="50%" stopColor="#DA56D0" />
          <stop offset="100%" stopColor="#6086FF" />
        </linearGradient>
        <radialGradient id="pulseCoreGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="35%" stopColor="#FF47C2" />
          <stop offset="80%" stopColor="#6086FF" />
          <stop offset="100%" stopColor="#6086FF" stopOpacity="0" />
        </radialGradient>
        <filter id="pulseGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ─── Halo (breathing) ─── */}
      <g>
        <circle cx="0" cy="0" r="190" fill="url(#pulseHalo)">
          <animate attributeName="opacity" values="0.55;1;0.55" dur="4.2s" repeatCount="indefinite" />
        </circle>
        <animateTransform attributeName="transform" type="scale" values="1;1.05;1" dur="4.2s" repeatCount="indefinite" />
      </g>

      {/* ─── Faint reference rings ─── */}
      <circle cx="0" cy="0" r="60" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
      <circle cx="0" cy="0" r="110" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
      <circle cx="0" cy="0" r="160" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />

      {/* ─── Static tick marks (24 around outer ring, every 6th brighter) ─── */}
      {Array.from({ length: 24 }).map((_, i) => (
        <line
          key={`tick-${i}`}
          x1="0"
          y1="-170"
          x2="0"
          y2={i % 6 === 0 ? "-182" : "-176"}
          stroke={i % 6 === 0 ? "rgba(218, 86, 208, 0.6)" : "rgba(218, 86, 208, 0.2)"}
          strokeWidth="1"
          strokeLinecap="round"
          transform={`rotate(${i * 15})`}
        />
      ))}

      {/* ─── Radar sweep — the validator block tick ─── */}
      <g>
        <line
          x1="0"
          y1="-165"
          x2="0"
          y2="-186"
          stroke="#62FFD0"
          strokeWidth="2.5"
          strokeLinecap="round"
          filter="url(#pulseGlow)"
        />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 0 0"
          to="360 0 0"
          dur="6s"
          repeatCount="indefinite"
        />
      </g>

      {/* ─── Pulse rings (3 staggered, emanating from core) ─── */}
      <circle cx="0" cy="0" fill="none" stroke="url(#pulseRingGrad)" strokeWidth="1.8" opacity="0">
        <animate attributeName="r" from="22" to="180" dur="3.6s" repeatCount="indefinite" begin="0s" />
        <animate attributeName="opacity" values="0;0.9;0" dur="3.6s" repeatCount="indefinite" begin="0s" />
        <animate attributeName="stroke-width" from="2.6" to="0.4" dur="3.6s" repeatCount="indefinite" begin="0s" />
      </circle>
      <circle cx="0" cy="0" fill="none" stroke="url(#pulseRingGrad)" strokeWidth="1.8" opacity="0">
        <animate attributeName="r" from="22" to="180" dur="3.6s" repeatCount="indefinite" begin="1.2s" />
        <animate attributeName="opacity" values="0;0.85;0" dur="3.6s" repeatCount="indefinite" begin="1.2s" />
        <animate attributeName="stroke-width" from="2.6" to="0.4" dur="3.6s" repeatCount="indefinite" begin="1.2s" />
      </circle>
      <circle cx="0" cy="0" fill="none" stroke="url(#pulseRingGrad)" strokeWidth="1.8" opacity="0">
        <animate attributeName="r" from="22" to="180" dur="3.6s" repeatCount="indefinite" begin="2.4s" />
        <animate attributeName="opacity" values="0;0.8;0" dur="3.6s" repeatCount="indefinite" begin="2.4s" />
        <animate attributeName="stroke-width" from="2.6" to="0.4" dur="3.6s" repeatCount="indefinite" begin="2.4s" />
      </circle>

      {/* ─── Orbital nodes — 4 collaterals at different radii/speeds ─── */}
      <g>
        <circle cx="60" cy="0" r="3.5" fill="#62FFD0" filter="url(#pulseGlow)" />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 0 0"
          to="360 0 0"
          dur="18s"
          repeatCount="indefinite"
        />
      </g>
      <g>
        <circle cx="110" cy="0" r="3" fill="#DA56D0" filter="url(#pulseGlow)" />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="90 0 0"
          to="-270 0 0"
          dur="26s"
          repeatCount="indefinite"
        />
      </g>
      <g>
        <circle cx="160" cy="0" r="2.5" fill="#6086FF" filter="url(#pulseGlow)" />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="200 0 0"
          to="560 0 0"
          dur="34s"
          repeatCount="indefinite"
        />
      </g>
      <g>
        <circle cx="85" cy="0" r="2" fill="#FFD447" filter="url(#pulseGlow)" />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="320 0 0"
          to="-40 0 0"
          dur="14s"
          repeatCount="indefinite"
        />
      </g>

      {/* ─── Inner core glow (breathing) ─── */}
      <g>
        <circle cx="0" cy="0" r="40" fill="url(#pulseCoreGrad)" opacity="0.6">
          <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1.6s" repeatCount="indefinite" />
        </circle>
        <animateTransform attributeName="transform" type="scale" values="0.95;1.22;0.95" dur="1.6s" repeatCount="indefinite" />
      </g>

      {/* ─── Central core (the pulse) ─── */}
      <g>
        <circle cx="0" cy="0" r="22" fill="url(#pulseCoreGrad)" />
        <circle cx="0" cy="0" r="9" fill="#FFFFFF" filter="url(#pulseGlow)">
          <animate attributeName="opacity" values="0.55;1;0.55" dur="1.2s" repeatCount="indefinite" />
        </circle>
        <animateTransform attributeName="transform" type="scale" values="1;1.08;1" dur="1.6s" repeatCount="indefinite" />
      </g>
    </svg>
  );
}
